import { A } from '@solidjs/router';
import { type JSX, Match, Show, Switch } from 'solid-js';
import { t } from '../i18n';
import type { StreamRefusal } from '../lib/appendLogStream';
import { type Me, useMe } from '../lib/session';
import { buttonClass } from './ui/Button';

// WHY this run is not on screen, when the stream refused rather than dropped.
//
// This is the incident that started the work. An operator opened a run they were
// an owner of — under ANOTHER of their orgs — and got "Couldn't load this run."
// plus a Try again that could only ever 404 again, because `!res.ok` collapsed a
// foreign run id, a 503 and a dropped socket into one `error`. Nothing on the
// page could tell them the run was one org switch away.
//
// Two rules:
//
//   - A 404 IS AN ANSWER (`apps/dashboard/CLAUDE.md`, the `RepoPendingTab` rule),
//     so it renders as its own line with NO retry. The API folds "not yours" and
//     "no such run" into one 404 on purpose — it will not confirm a stranger's
//     run exists — so the copy names the readings it cannot rule out rather than
//     picking one.
//   - The way out is offered from what the client ALREADY holds. `/v1/me` carries
//     the caller's memberships and `is_platform_admin`, so an operator is pointed
//     at the cross-org admin view and a member at their other orgs. Neither is an
//     authorization: the destination re-asks the server.
//
// Every other refusal falls through to `<ErrorState/>`, which keeps its Retry —
// a 503 and a dropped socket are worth retrying, which is exactly the difference
// this component exists to draw.

/**
 * Is this refusal an ANSWER (render it as one, no Retry) or a FAULT (fall through
 * to `<ErrorState/>`, which keeps its Retry)?
 *
 * THREE are answers, and the third was missing. A 404 — the server resolved the
 * id and refused, and retrying reproduces it exactly. Having no session, where
 * nothing was asked. And a **403**: the credential is fine and this caller may
 * not, so the re-ask carries the same scopes and the same `X-Dz-Account` claim
 * and is refused identically. Both these routes are org-path-scoped, so a
 * refused tenant claim is reachable on either — and until this line it landed in
 * `<ErrorState/>` with a Try again that could only 403 again, which is the exact
 * shape of the incident at the top of this file, one status over.
 *
 * A 503, a 500 and a 429 stay FAULTS and keep their retry; folding them in here
 * would replace one collapsed state with another. This is the stream-side
 * spelling of `isForbidden`/`isUnauthorized` (`lib/api-errors.ts`), which draws
 * the same line for a query.
 */
export function isAnsweredRefusal(refusal: StreamRefusal): boolean {
  if (refusal.kind === 'no_session') return true;
  return refusal.status === 404 || refusal.status === 403;
}

export type RunStreamRefusalProps = {
  readonly refusal: StreamRefusal;
  readonly runId: string;
  /** Injectable for tests; the route passes its own `props.token`. */
  readonly token: string | null;
};

/**
 * Orgs beyond the one being viewed — the "it may be over there" hint's evidence.
 * Shared with `<SessionStreamRefusal/>`, the session-side sibling, so the two
 * refusal views cannot disagree about when that hint is warranted.
 */
export function otherOrgCount(me: Me | undefined): number {
  const memberships = me?.memberships ?? [];
  return Math.max(0, memberships.length - 1);
}

export function RunStreamRefusal(props: RunStreamRefusalProps): JSX.Element {
  const me = useMe(props.token);
  return (
    <div class="empty-state" role="status">
      <Switch
        fallback={
          <>
            {/* No credential: no request was sent, so nothing is known about the
                run. "No events for this run yet." was a claim about a run made
                from a request nobody made — and prod has ZERO runs with zero
                events, so every real sighting of that string was a lie. */}
            <p class="empty-state__message">{t('fleet.run_signed_out')}</p>
            <p class="timeline__meta">{t('fleet.run_signed_out_desc')}</p>
          </>
        }
      >
        {/* A 403 carries NONE of the 404's hints, and that is deliberate. The
            404 folds "not yours" into "no such run", so the run may genuinely be
            one org switch away; a 403 is the tenant claim itself being refused —
            the caller is not in the org they asked as — which says nothing about
            where the run is. Offering the cross-org hint here would send a
            reader looking for a run this answer never mentioned. */}
        <Match when={props.refusal.kind === 'http' && props.refusal.status === 403}>
          <p class="empty-state__message">{t('fleet.run_denied')}</p>
        </Match>
        <Match when={props.refusal.kind === 'http'}>
          <p class="empty-state__message">{t('fleet.run_not_found')}</p>
          <Show when={me.data?.is_platform_admin}>
            <A
              class={buttonClass({ variant: 'outline', size: 'sm' })}
              href={`/admin/runs/${props.runId}`}
            >
              {t('fleet.run_not_found_admin')}
            </A>
          </Show>
          <Show when={!me.data?.is_platform_admin && otherOrgCount(me.data) > 0}>
            <p class="timeline__meta">{t('fleet.run_not_found_other_orgs')}</p>
          </Show>
        </Match>
      </Switch>
    </div>
  );
}
